import { useEffect, useRef, useState } from 'react';

import {
  freezeFrameHeld,
  freezeFrameHeldMs,
  freezeFrameSurface,
  releaseFreezeFrame,
  shouldReleaseFreeze,
} from '../freeze-frame.js';

/**
 * 채널 전환의 빈 창을 **직전 채널의 마지막 프레임**으로 덮는다 (v28 · `freeze-frame.ts`).
 *
 * ── 왜 마운트에서 한 번만 묻는가 ────────────────────────────
 *
 * 잡아 둔 프레임은 **모듈 스코프**에 있다(`playback-buffers.ts` 와 같은 이유 — 살아남아야
 * 하는 것이 정확히 재마운트를 건너서다). 그래서 이 컴포넌트는 `ChannelGate` 의 빈 렌더에서
 * 한 번 서고 새 화면에서 다시 서는데, **그 사이 그림은 그대로다.** 구독을 만들지 않는 것이
 * 그 덕이다.
 *
 * ── 언제 놓는가 ─────────────────────────────────────────────
 *
 * 판정은 `shouldReleaseFreeze` 가 갖고, 여기서는 **재료 둘**만 읽는다 — 같은 무대 안의
 * `<video>` 가 실제로 프레임을 넘기고 있는가(`currentTime` 이 움직이는가)와, 얼마나
 * 들고 있었는가. `readyState` 나 `play()` 의 성공으로 판정하지 않는다 — **둘 다 첫
 * 프레임이 붙기 전에 참이 되어** 잡은 그림을 검은 화면으로 바꿔 놓는다.
 */
export function FreezeFrame({
  /**
   * v29. **걷히는 순간.** 부르는 쪽이 그동안 무언가를 숨기고 있다면 이것으로 되돌린다 —
   * 잡은 프레임의 유무는 **모듈 상태**라 렌더에서 읽기만 해서는 다시 그려지지 않는다.
   */
  onRelease,
}: {
  onRelease?: () => void;
} = {}) {
  const [held, setHeld] = useState(() => freezeFrameHeld());
  const mount = useRef<HTMLDivElement | null>(null);
  /*
   * ★ **의존성에 넣지 않는다.** 부르는 쪽이 인라인 화살표를 주므로 렌더마다 신원이
   * 달라지고, 넣으면 **4Hz 로 도는 `timeupdate` 마다 rAF 루프가 다시 선다.**
   */
  const release = useRef(onRelease);
  release.current = onRelease;

  useEffect(() => {
    if (!held) return;
    const host = mount.current;
    if (host === null) return;
    host.appendChild(freezeFrameSurface());

    let raf = 0;
    // **무대 안의 것만 본다.** 문서 어딘가의 보관함(`#bloom-buffers`)에 있는 엘리먼트는
    // 아직 이 화면의 것이 아니다 — 그것으로 판정하면 옛 채널의 마지막 초가 그림을 걷는다.
    const stage = host.closest('.stage');
    const seen = new WeakMap<HTMLVideoElement, number>();
    const tick = () => {
      let painting = false;
      for (const v of stage?.querySelectorAll('video') ?? []) {
        const before = seen.get(v);
        seen.set(v, v.currentTime);
        if (before !== undefined && v.currentTime !== before && v.readyState >= 2) painting = true;
      }
      if (shouldReleaseFreeze({ painting, heldMs: freezeFrameHeldMs() })) {
        releaseFreezeFrame();
        setHeld(false);
        release.current?.();
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [held]);

  if (!held) return null;
  // 캔버스를 **직접 렌더하지 않는다** — 그것은 React 트리 밖의 엘리먼트이고, 여기서
  // 만들면 전환마다 새 캔버스가 되어 「모듈 스코프」의 뜻이 사라진다.
  return <div className="stage__freeze" ref={mount} data-testid="freeze-frame" />;
}
